import React, { useState, useContext } from "react";
import api from "../api/client";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  Fade,
  Stack,
  InputAdornment,
} from "@mui/material";
import {
  LockRounded,
  PersonRounded,
  ArrowForwardRounded,
} from "@mui/icons-material";
import { AuthContext } from "../context/AuthContext";
import { Kicker, GradientText, Rule, Glow, Tag } from "../theme/scala";
import { BRAND, GRADIENT, FONT, GLASS, SHADOW } from "../theme/brand";

// Giriş ekranı, ana sitenin hero dilini kullanır: solda mono etiket + sıkı
// harf aralıklı gradyan başlık, sağda cam yüzeyli form. Zeminde ızgara dokusu
// ve iki ışık lekesi — sitedeki bölüm açılışlarıyla aynı kurgu.

const HIGHLIGHTS = [
  "5 sunucu · 7/24 izleme",
  "Otomatik saldırı engelleme",
  "Şifreli kimlik bilgileri",
];

export default function Login() {
  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await api.post("/login", credentials);
      if (res.data.success) {
        login(res.data.token);
        navigate("/");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Bağlantı başarısız.");
    } finally {
      setBusy(false);
    }
  };

  const field = { fullWidth: true, size: "medium" };

  return (
    <Box
      className="grid-lines"
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        px: { xs: 2.5, md: 6 },
        py: 6,
        bgcolor: BRAND.void,
      }}
    >
      <Glow tone="cyan" size={620} sx={{ top: "-18%", left: "-10%" }} />
      <Glow tone="ember" size={420} sx={{ bottom: "-14%", right: "-8%", opacity: 0.55 }} />

      <Fade in timeout={800}>
        <Box
          sx={{
            position: "relative",
            width: "100%",
            maxWidth: 1080,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1.05fr 0.95fr" },
            gap: { xs: 5, md: 8 },
            alignItems: "center",
          }}
        >
          {/* ── Sol: marka anlatısı ── */}
          <Box sx={{ display: { xs: "none", md: "block" } }}>
            <Kicker index="01">Güvenli Erişim</Kicker>

            <Typography
              variant="h3"
              sx={{
                mt: 3,
                fontSize: { md: "3.4rem", lg: "4rem" },
                letterSpacing: "-0.05em",
                lineHeight: 0.95,
                fontWeight: 600,
              }}
            >
              Altyapının
              <br />
              <GradientText>kontrol merkezi</GradientText>
            </Typography>

            <Typography sx={{ mt: 3, maxWidth: 420, color: BRAND.inkDim, fontSize: "1rem", lineHeight: 1.7 }}>
              Sunucu sağlığı, servisler, SSL süreleri ve saldırı savunması tek ekranda.
              Visione izler, tespit eder ve gerektiğinde kendisi müdahale eder.
            </Typography>

            <Rule sx={{ my: 4, maxWidth: 420 }} />

            <Stack spacing={1.5}>
              {HIGHLIGHTS.map((item) => (
                <Box key={item} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box
                    aria-hidden
                    sx={{ height: "1px", width: 20, background: GRADIENT.kickerRule, flexShrink: 0 }}
                  />
                  <Typography
                    sx={{
                      fontFamily: FONT.mono,
                      fontSize: "0.75rem",
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      color: BRAND.inkFaint,
                    }}
                  >
                    {item}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>

          {/* ── Sağ: giriş formu ── */}
          <Box
            sx={{
              ...GLASS,
              borderRadius: "20px",
              boxShadow: SHADOW.raised,
              p: { xs: 3.5, md: 5 },
              width: "100%",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.75, mb: 4 }}>
              <Box
                component="img"
                src="/logo.png"
                alt="Visione"
                sx={{ width: 44, height: 44, borderRadius: "12px", objectFit: "cover" }}
              />
              <Box>
                <Typography sx={{ fontSize: "1.35rem", fontWeight: 600, letterSpacing: "-0.035em", lineHeight: 1.1 }}>
                  Visione<Box component="span" sx={{ color: BRAND.ember }}>.</Box>
                </Typography>
                <Typography
                  sx={{
                    fontFamily: FONT.mono,
                    fontSize: "0.625rem",
                    letterSpacing: "0.22em",
                    color: BRAND.inkFaint,
                  }}
                >
                  SCALAVISIONE
                </Typography>
              </Box>
              <Box sx={{ flexGrow: 1 }} />
              <Tag accent>Panel</Tag>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleLogin}>
              <Stack spacing={2}>
                <TextField
                  {...field}
                  label="Kullanıcı Adı"
                  autoComplete="username"
                  value={credentials.username}
                  onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                  slotProps={{
                    inputLabel: { sx: { fontSize: "0.875rem" } },
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonRounded sx={{ color: BRAND.inkFaint, fontSize: 19 }} />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
                <TextField
                  {...field}
                  label="Şifre"
                  type="password"
                  autoComplete="current-password"
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  slotProps={{
                    inputLabel: { sx: { fontSize: "0.875rem" } },
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockRounded sx={{ color: BRAND.inkFaint, fontSize: 19 }} />
                        </InputAdornment>
                      ),
                    },
                  }}
                />

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  type="submit"
                  disabled={busy}
                  endIcon={<ArrowForwardRounded sx={{ fontSize: 18 }} />}
                  sx={{ mt: 1.5 }}
                >
                  {busy ? "Doğrulanıyor…" : "Sisteme Giriş Yap"}
                </Button>
              </Stack>
            </form>

            <Rule sx={{ mt: 4, mb: 2 }} />

            <Typography
              sx={{
                fontFamily: FONT.mono,
                fontSize: "0.625rem",
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: BRAND.inkFaint,
                textAlign: "center",
              }}
            >
              Yetkisiz erişim denemeleri kaydedilir
            </Typography>
          </Box>
        </Box>
      </Fade>
    </Box>
  );
}
